import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { valueRender } from '@repo/toolkit/conditional-render'
import { DateFormatter } from '@repo/toolkit/date'
import { StringFormatter } from '@repo/toolkit/string'
import { useNavigator } from '@repo/toolkit/use-navigator'
import Badge from '@repo/ui/badge'
import DataTable, { Column, DataTableProps } from '@repo/ui/data-table'
import Typography from '@repo/ui/typography'

import { Paths } from '../../../lib/constants/path'
import { genderOneLetterMapping } from '../../../lib/types/enums/common/gender.enum'
import { AffectedStaffLight } from '../../../lib/types/models/affectedStaff/affected-staff'
import { formatPhoneNumber } from '../../../utils/functions/format-phone-number'
import { QualificationTags } from '../../elements/qualification-badge'

interface TripFolderStaffPassengersListProps extends Pick<
  DataTableProps<AffectedStaffLight, undefined>,
  'selectable' | 'selectedData' | 'onSelectedDataChange'
> {
  data: AffectedStaffLight[]
  isLoading?: boolean
}

const TripFolderStaffPassengersList = ({
  data,
  isLoading,
  ...tableProps
}: TripFolderStaffPassengersListProps) => {
  const { navigate } = useNavigator()

  const columns: Column<AffectedStaffLight>[] = [
    {
      accessorKey: 'reservationNumber',
      header: 'N° Résa',
      cell: ({ row }) => valueRender(row.original.reservationNumber)
    },
    {
      accessorKey: 'staffRequest',
      header: 'Centre',
      cell: ({ row }) => (
        <div className="flex flex-col gap-2 py-2">
          {row.original.staffRequest.centers.map((center, index) => (
            <Typography variant="secondary-body" key={index}>
              {center.name}
            </Typography>
          ))}
        </div>
      )
    },
    {
      accessorKey: 'lastName',
      header: 'Nom',
      cell: ({ row }) => (
        <Typography
          variant="secondary-body"
          color={tailwindColors.blue.DEFAULT}
          className="underline cursor-pointer"
          onClick={() =>
            navigate(Paths.STAFF_UPDATE_PAGE.replace(':id', row.original.staff.id.toString()))
          }
        >
          {row.original.staff.lastName.toUpperCase()}
        </Typography>
      )
    },
    {
      accessorKey: 'firstName',
      header: 'Prénom',
      cell: ({ row }) => (
        <Typography
          variant="secondary-body"
          color={tailwindColors.blue.DEFAULT}
          className="underline cursor-pointer"
          onClick={() =>
            navigate(Paths.STAFF_UPDATE_PAGE.replace(':id', row.original.staff.id.toString()))
          }
        >
          {StringFormatter.capitalize(row.original.staff.firstName)}
        </Typography>
      )
    },
    {
      accessorKey: 'staff.birthDate',
      header: 'Âge',
      cell: ({ row }) =>
        valueRender(row.original.staff.birthDate, (birthdate) =>
          DateFormatter.calculateAge(birthdate).toString()
        )
    },
    {
      accessorKey: 'gender',
      header: 'Sexe',
      cell: ({ row }) =>
        valueRender(row.original.staff.gender, (gender) => genderOneLetterMapping[gender].label)
    },
    {
      accessorKey: 'nationality',
      header: 'Nationalité',
      cell: ({ row }) => valueRender(row.original.staff.nationality)
    },
    {
      accessorKey: 'qualification',
      header: 'Qualification',
      cell: ({ row }) => <QualificationTags qualifications={row.original.staff.qualifications} />
    },
    {
      accessorKey: 'phoneNumber',
      header: 'N° tél',
      cell: ({ row }) =>
        valueRender(row.original.staff.phoneNumber, (phoneNumber) =>
          formatPhoneNumber(phoneNumber)
        )
    }
  ]

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <Typography variant="text">Liste des encadrants</Typography>
        <Badge variant="blue" text={`Total encadrants : ${data.length}`} />
      </div>
      <DataTable<AffectedStaffLight>
        {...tableProps}
        columns={columns}
        data={data}
        isLoading={isLoading}
        paginated={false}
        rowCount={data.length}
        disableHover
      />
    </div>
  )
}

export default TripFolderStaffPassengersList
